import { Component, inject } from '@angular/core';
import { LoginService } from '../../services/login.service';
import { Entity, PermissionService } from '../../services/permission.service';
import { Params, Router } from '@angular/router';
import { isMatch } from 'lodash-es';

export enum SidebarState {
  Collapsed = 'COLLAPSED',
  Expanded = 'EXPANDED',
  ChildrenFromCollapsed = 'CHILDREN_FROM_COLLAPSED',
  ChildrenFromExpanded = 'CHILDREN_FROM_EXPANDED',
  CollapsedChildren = 'COLLAPSED_CHILDREN',
}
export interface NavItem {
  label: string;
  canShow: () => boolean;
  routerLink?: string;
  routerLinkParams?: Params;
  iconClass?: string;
  children?: NavItem[];
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent {
  private readonly login = inject(LoginService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  public readonly SidebarState = SidebarState;
  public state: SidebarState = SidebarState.Collapsed;
  public selectedParent: NavItem | null = null;

  public readonly navItems: NavItem[] = [
    {
      label: 'Sales Management',
      canShow: () => this.permissionService.canManage(Entity.Sales),
      routerLink: '/admin/list/sales',
      iconClass: 'icon--sales-management',
    },
    {
      label: 'Enterprise Management',
      canShow: () => this.permissionService.canManage(Entity.EnterpriseAdmin),
      iconClass: 'icon--enterprise',
      children: [
        {
          label: 'General',
          canShow: () => this.permissionService.canManage(Entity.EnterpriseAdmin),
          routerLink: '/admin/list/enterprise_admin',
        },
        {
          label: 'Corporate Domains',
          canShow: () => this.permissionService.canManage(Entity.Domains),
          routerLink: '/admin/list/domains'
        },
      ]
    },
    {
      label: 'Admin Management',
      canShow: () => this.permissionService.canManage(Entity.Accounts) || this.permissionService.canManage(Entity.SubrealmAccounts),
      iconClass: 'icon--admin-list',
      routerLink: '/admin/list/accounts',
    },
    {
      label: 'User Management',
      canShow: () => true,
      iconClass: 'icon--user-management',
      children: [
        {
          label: 'Invitations',
          canShow: () => this.permissionService.canManage(Entity.Invitations),
          routerLink: '/admin/list/invitations',
        },
        {
          label: 'Blocking',
          canShow: () => this.permissionService.canManage(Entity.Blocking),
          routerLink: '/admin/block',
        },
      ]
    },
    {
      label: 'IDP',
      canShow: () => this.permissionService.canManage(Entity.EnterpriseAdmin),
      iconClass: 'icon--idp',
      children: [
        {
          label: 'Directories',
          canShow: () => this.permissionService.canManage(Entity.Directories),
          routerLink: '/admin/directories',
        },
        {
          label: 'Applications',
          canShow: () => this.permissionService.canManage(Entity.Applications),
          routerLink: '/admin/list/apps',
        },
        {
          label: 'Access Groups',
          canShow: () => this.permissionService.canManage(Entity.AccessGroups),
          routerLink: '/admin/list/groups',
        },
      ]
    },
    {
      label: 'Support',
      canShow: () => this.permissionService.canManage(Entity.Support),
      routerLink: '/admin/support',
      iconClass: 'icon--support'
    },
    {
      label: 'Audit',
      canShow: () => this.permissionService.canManage(Entity.Audit),
      routerLink: '/admin/list/audit',
      iconClass: 'icon--audit',
    },
  ];

  public get showNavigation(): boolean {
    return !!this.login.user && this.navItems.some(item => item.canShow());
  }

  public get isRootNavCollapsed(): boolean {
    return this.state !== SidebarState.Expanded;
  }

  public get showChildNav(): boolean {
    return this.selectedParent && [SidebarState.ChildrenFromCollapsed, SidebarState.ChildrenFromExpanded].includes(this.state);
  }

  public get canCollapse(): boolean {
    return [SidebarState.Expanded, SidebarState.ChildrenFromExpanded, SidebarState.ChildrenFromCollapsed].includes(this.state);
  }

  public toggleCollapse(): void {
    switch(this.state) {
      case SidebarState.Collapsed:
        this.state = SidebarState.Expanded;
        break;
      case SidebarState.Expanded:
        this.state = SidebarState.Collapsed;
        break;
      case SidebarState.ChildrenFromCollapsed:
        this.state = SidebarState.CollapsedChildren;
        break;
      case SidebarState.ChildrenFromExpanded:
        this.state = SidebarState.CollapsedChildren;
        break;
      case SidebarState.CollapsedChildren:
        this.state = SidebarState.ChildrenFromCollapsed;
        break;
    }
  }

  public handleRootNavItemClick(item: NavItem): void {
    switch(this.state) {
      case SidebarState.Collapsed:
        if (item.children) {
          this.handleParentClick(item);
          this.state = SidebarState.ChildrenFromCollapsed;
        }
        break;
      case SidebarState.Expanded:
        if (item.children) {
          this.handleParentClick(item);
          this.state = SidebarState.ChildrenFromExpanded;
        }
        break;
      case SidebarState.ChildrenFromCollapsed:
        if (item.children) {
          this.handleParentClick(item);
        } else {
          this.state = SidebarState.Collapsed;
          this.selectedParent = null;
        }
        break;
      case SidebarState.ChildrenFromExpanded:
        if (item.children) {
          this.handleParentClick(item);
        } else {
          this.state = SidebarState.Expanded;
          this.selectedParent = null;
        }
        break;
      case SidebarState.CollapsedChildren:
        if (item.children) {
          this.handleParentClick(item);
          this.state = SidebarState.ChildrenFromCollapsed;
        } else {
          this.state = SidebarState.Collapsed;
          this.selectedParent = null;
        }
        break;
    }
  }

  public isActiveParent(item: NavItem): boolean {
    const path = this.router.url;
    const queryParams = this.router.parseUrl(path).queryParams;

    return !!item.children?.some(child => {
      return path.includes(child.routerLink) && (!child.routerLinkParams || isMatch(child.routerLinkParams, queryParams));
    });
  }

  private handleParentClick(item: NavItem): void {
    if (item === this.selectedParent) {
      this.toggleCollapse();
    } else {
      this.selectedParent = item;
      this.navigateToFirstChild(item);
    }
  }

  private navigateToFirstChild(item: NavItem): void {
    const firstShownItem = item.children?.find(element => element.canShow());
    if (firstShownItem) {
      this.router.navigate([firstShownItem.routerLink], {queryParams: firstShownItem.routerLinkParams});
    }
  }
}
